// src/features/upload/components/UploadQueueStatusBanner.tsx

import { Alert, Button, Group, Progress, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconUpload } from '@tabler/icons-react';
import { UploadUtils } from '../UploadUtils';
import type { UploadItem } from '@/lib/types/Upload';

interface UploadQueueStatusBannerProps {
  items: UploadItem[];
  /** Show a Sign out button in the all-received state (upload-only users). */
  showSignOut: boolean;
  onSignOut: () => void;
}

/**
 * Aggregate queue status for the full-page upload experience. The key message
 * is the all-received state: uploaders must know that once files reach the
 * server they can leave — processing needs no open tab.
 */
export function UploadQueueStatusBanner({ items, showSignOut, onSignOut }: UploadQueueStatusBannerProps) {
  const phase = UploadUtils.getQueuePhase(items);
  if (phase === 'empty') return null;

  const counts = UploadUtils.getQueueCounts(items);

  if (phase === 'sending') {
    const sendingItems = items.filter(
      (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading'
    );
    const totalBytes = sendingItems.reduce((sum, i) => sum + i.bytesTotal, 0);
    const uploadedBytes = sendingItems.reduce((sum, i) => sum + i.bytesUploaded, 0);
    const progress = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

    return (
      <Alert
        icon={<IconUpload size={18} />}
        color="blue"
        variant="light"
        title={`Sending ${counts.inFlight} file${counts.inFlight !== 1 ? 's' : ''}…`}
      >
        <Stack gap="xs">
          <Text size="sm">Keep this tab open until sending finishes.</Text>
          <Progress value={progress} size="sm" color="blue" />
        </Stack>
      </Alert>
    );
  }

  if (phase === 'all-received') {
    const parts: string[] = [];
    if (counts.processing > 0) parts.push(`${counts.processing} processing`);
    if (counts.ready > 0) parts.push(`${counts.ready} ready`);

    return (
      <Alert icon={<IconCircleCheck size={18} />} color="green" variant="light" title="All files received">
        <Stack gap="xs">
          <Text size="sm">
            Processing continues automatically on our servers — you can safely sign out or close this tab.
          </Text>
          {parts.length > 0 && (
            <Text size="sm" c="dimmed">
              {parts.join(' · ')}
            </Text>
          )}
          {showSignOut && (
            <Group>
              <Button size="xs" variant="default" onClick={onSignOut}>
                Sign out
              </Button>
            </Group>
          )}
        </Stack>
      </Alert>
    );
  }

  // 'attention' — failures present; other files may have been received.
  // Failed files never reached the server: say so before the user signs out.
  const receivedCount = counts.processing + counts.ready;
  return (
    <Alert
      icon={<IconAlertTriangle size={18} />}
      color="orange"
      variant="light"
      title={`${counts.failed} file${counts.failed !== 1 ? 's' : ''} did not finish sending`}
    >
      <Stack gap="xs">
        <Text size="sm">
          {counts.failed !== 1 ? 'These files' : 'This file'} did not reach us — use{' '}
          <strong>Retry failed</strong> below before you sign out or close this tab. Retried
          uploads continue from where they left off.
        </Text>
        {receivedCount > 0 && (
          <Text size="sm" c="dimmed">
            {receivedCount} other file{receivedCount !== 1 ? 's were' : ' was'} received and{' '}
            {receivedCount !== 1 ? 'are' : 'is'} safe — processing continues on our servers.
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
